import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["conditional", "startsAt", "endsAt", "duration", "forward", "heading", "errors", "initialFocus", "modality", "lessonType"]
  static values = { durationTemplate: String, restorePreferences: Boolean }

  connect() {
    this.restoreLastPreferences()
    this.refresh()
    const initialFocus = this.initialFocusTargets.find((element) => !element.closest("[hidden]"))
    const focusTarget = this.hasErrorsTarget ? this.errorsTarget : initialFocus || this.headingTargets.find((element) => !element.closest("[hidden]"))
    if (focusTarget) {
      focusTarget.focus({ preventScroll: true })
      window.scrollTo({ top: 0 })
    }
  }

  restoreLastPreferences() {
    if (!this.restorePreferencesValue) return

    this.restorePreferencesValue = false
    this.restoreChoice(this.hasModalityTarget ? this.modalityTarget : null, "teacher-panel:last-class-modality")
    this.restoreChoice(this.hasLessonTypeTarget ? this.lessonTypeTarget : null, "teacher-panel:last-class-lesson-type")
  }

  restoreChoice(control, key) {
    if (!control) return

    try {
      const saved = localStorage.getItem(key)
      if (Array.from(control.options).some((option) => option.value === saved && !option.disabled)) {
        control.value = saved
      }
    } catch {
      // Storage may be unavailable in private or restricted browsers.
    }
  }

  rememberModality() {
    if (this.hasModalityTarget) this.rememberChoice(this.modalityTarget, "teacher-panel:last-class-modality")
  }

  rememberLessonType() {
    if (this.hasLessonTypeTarget) this.rememberChoice(this.lessonTypeTarget, "teacher-panel:last-class-lesson-type")
  }

  rememberChoice(control, key) {
    try {
      localStorage.setItem(key, control.value)
    } catch {
      // Keep the form usable when browser storage is unavailable.
    }
  }

  continueStep(event) {
    if (event.target.closest(".ts-wrapper")) return
    if (event.isComposing || !event.target.matches('input[type="text"], input[type="url"], input[type="date"], input[type="time"], input[type="number"]')) return

    event.preventDefault()
    if (this.hasForwardTarget && !this.forwardTarget.disabled) this.element.requestSubmit(this.forwardTarget)
  }

  refresh() {
    this.conditionalTargets.forEach((element) => {
      const controls = this.element.querySelectorAll(`[name="class_publication[${element.dataset.whenField}]"]`)
      const control = Array.from(controls).find((input) => input.type !== "hidden" && (input.type !== "radio" || input.checked)) || controls[0]
      const value = control?.type === "checkbox" ? String(control.checked) : control?.value
      element.hidden = !element.dataset.whenValues.split(" ").includes(value)
    })
    this.updateDuration()
  }

  suggestEndTime() {
    if (!this.hasStartsAtTarget || !this.hasEndsAtTarget || this.endsAtTarget.value) return

    const start = this.minutes(this.startsAtTarget.value)
    if (start === null) return

    const end = Math.min(start + 60, 23 * 60 + 59)
    this.endsAtTarget.value = `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`
    this.updateDuration()
  }

  updateDuration() {
    if (!this.hasDurationTarget || !this.hasStartsAtTarget || !this.hasEndsAtTarget) return

    const start = this.minutes(this.startsAtTarget.value)
    const end = this.minutes(this.endsAtTarget.value)
    this.durationTarget.textContent = start !== null && end !== null && end > start
      ? this.durationTemplateValue.replace("%minutes%", end - start)
      : ""
  }

  minutes(value) {
    if (!/^\d{2}:\d{2}$/.test(value)) return null

    const [hours, minutes] = value.split(":").map(Number)
    return hours * 60 + minutes
  }
}
